import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquareHeart, Camera, Loader2, X as XIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FieldLabel } from '@/components/editorial';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import type { FeedbackType } from '@/types/api';
import { formatNumber } from '@/i18n/intl';

// The five kinds, in display order. Each label and hint is
// `feedback:dialog.types.<value>.{label,hint}` (#2109 R3 L8).
// 2026-08-18 — a question becomes a tracked GitHub issue on the Ledger's
// open-questions board once an admin promotes it, and the submitter is told
// the issue number. An idea just stays feedback.
const TYPES: FeedbackType[] = ['bug', 'idea', 'question', 'praise', 'other'];

/**
 * Derive a Tailwind-aligned breakpoint label from viewport width so admins
 * can read "Mobile" / "Tablet" / "Desktop" at a glance instead of decoding
 * a raw pixel pair.
 */
function describeViewport(width: number): { device: 'Mobile' | 'Tablet' | 'Desktop'; breakpoint: string } {
  if (width < 640)  return { device: 'Mobile',  breakpoint: 'xs' };
  if (width < 768)  return { device: 'Mobile',  breakpoint: 'sm' };
  if (width < 1024) return { device: 'Tablet',  breakpoint: 'md' };
  if (width < 1280) return { device: 'Desktop', breakpoint: 'lg' };
  if (width < 1536) return { device: 'Desktop', breakpoint: 'xl' };
  return { device: 'Desktop', breakpoint: '2xl' };
}

/**
 * Re-encode a base64 JPEG/PNG data URL through a downscaled canvas, capped
 * at `maxWidth`. Keeps the screenshot under our 3MB backend cap regardless
 * of the user's display size.
 */
async function downscaleDataUrl(dataUrl: string, maxWidth: number, quality = 0.78): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });
  if (img.naturalWidth <= maxWidth) return dataUrl;
  const scale = maxWidth / img.naturalWidth;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality);
}

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { toast } = useToast();
  const { t, i18n } = useTranslation(['feedback', 'common']);

  const [type, setType] = useState<FeedbackType>('idea');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Screenshot opt-in lifecycle: capturing closes the dialog visually so it
  // doesn't appear in the screenshot, while preserving in-progress form state.
  const [attachShot, setAttachShot] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);

  const reset = () => {
    setType('idea');
    setMessage('');
    setSubmitting(false);
    setAttachShot(false);
    setCapturing(false);
    setScreenshot(null);
  };

  // Dialog close → reset only if we're not in the middle of capturing.
  // While capturing, `open` is forced false to hide the dialog from the
  // screenshot, but we keep form state so the user returns to where they were.
  const handleOpenChange = (next: boolean) => {
    if (capturing) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const captureScreenshot = async () => {
    setCapturing(true);
    try {
      // Wait for the dialog's close animation to play out (Radix default ~200ms).
      await new Promise((r) => setTimeout(r, 320));
      // html-to-image renders via SVG <foreignObject>, which handles modern
      // CSS (oklch, color-mix, lab) that html2canvas chokes on.
      const { toJpeg } = await import('html-to-image');
      const raw = await toJpeg(document.body, {
        quality: 0.85,
        backgroundColor: '#ffffff',
        // Cap pixel ratio so a hi-DPI display doesn't produce an enormous image.
        pixelRatio: Math.min(window.devicePixelRatio, 1.5),
        cacheBust: true,
        // Skip noisy / huge nodes we don't want in the report.
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE') return false;
          return true;
        },
      });

      // Downscale to <= 1280px wide for a sane payload. Try a stricter pass
      // if we're still over the 3MB cap.
      let final = await downscaleDataUrl(raw, 1280, 0.78);
      if (final.length > 3_000_000) final = await downscaleDataUrl(raw, 960, 0.7);
      if (final.length > 3_000_000) {
        toast({
          title: t('toast.tooLargeTitle'),
          description: t('toast.tooLargeDescription'),
          variant: 'destructive',
        });
        setAttachShot(false);
        return;
      }
      setScreenshot(final);
    } catch (err) {
       
      console.error('[feedback] screenshot capture failed:', err);
      toast({
        title: t('toast.captureFailedTitle'),
        description: t('toast.captureFailedDescription'),
        variant: 'destructive',
      });
      setAttachShot(false);
    } finally {
      setCapturing(false);
    }
  };

  const handleToggleAttach = (next: boolean) => {
    if (capturing || submitting) return;
    if (!next) {
      setAttachShot(false);
      setScreenshot(null);
      return;
    }
    setAttachShot(true);
    void captureScreenshot();
  };

  const handleSubmit = async () => {
    if (message.trim().length < 3) return;
    setSubmitting(true);
    try {
      const { device, breakpoint } = describeViewport(window.innerWidth);
      await api.feedback.submit({
        message: message.trim(),
        type,
        context: {
          route: pathname,
          user_agent: navigator.userAgent,
          app_version: import.meta.env.VITE_APP_VERSION ?? 'dev',
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          device,
          breakpoint,
          orientation: window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape',
          device_pixel_ratio: window.devicePixelRatio,
          role: user?.role,
        },
        screenshot: attachShot && screenshot ? screenshot : undefined,
      });
      toast({
        title: t('toast.sentTitle'),
        description: attachShot && screenshot
          ? t('toast.sentWithScreenshot')
          : t('toast.sent'),
      });
      handleOpenChange(false);
    } catch (err: any) {
      // R3-B11: the server's sentence is English, so only an English reader
      // sees it; everyone else gets the localized fallback line.
      const english = i18n.language === 'en' || i18n.language.startsWith('en-');
      const description = (english ? err?.response?.data?.message : undefined) || t('toast.sendFailedDescription');
      toast({ title: t('toast.sendFailedTitle'), description, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open && !capturing} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareHeart className="h-4 w-4 text-stevens-indigo" />
            {t('dialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('dialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 -mx-6 px-6 space-y-4">
          <div className="space-y-2">
            <FieldLabel>{t('dialog.typeLabel')}</FieldLabel>
            <RadioGroup
              value={type}
              onValueChange={(v) => setType(v as FeedbackType)}
              className="grid grid-cols-2 gap-2"
            >
              {TYPES.map((value) => (
                <label
                  key={value}
                  htmlFor={`fb-type-${value}`}
                  className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                    type === value
                      ? 'border-stevens-indigo bg-stevens-indigo/5'
                      : 'border-border hover:border-stevens-indigo/30'
                  }`}
                >
                  <RadioGroupItem id={`fb-type-${value}`} value={value} className="mt-1" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-body font-medium">{t(`dialog.types.${value}.label`)}</span>
                    <span className="block text-label text-stevens-ink-3">{t(`dialog.types.${value}.hint`)}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <FieldLabel htmlFor="fb-message">{t('dialog.messageLabel')}</FieldLabel>
            <Textarea
              id="fb-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('dialog.messagePlaceholder')}
              rows={6}
              maxLength={4000}
              autoFocus
            />
            <p className="text-label text-stevens-ink-3">
              {t('dialog.contextNote')}
            </p>
          </div>

          <div className="space-y-2 pt-1">
            <FieldLabel>{t('dialog.attachmentLabel')}</FieldLabel>
            <label
              htmlFor="fb-attach"
              className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                attachShot ? 'border-stevens-indigo bg-stevens-indigo/5' : 'border-border hover:border-stevens-indigo/30'
              }`}
            >
              <Checkbox
                id="fb-attach"
                checked={attachShot}
                onCheckedChange={(v) => handleToggleAttach(!!v)}
                className="mt-1"
                disabled={submitting}
              />
              <span className="flex-1 min-w-0">
                <span className="block text-body font-medium flex items-center gap-1.5">
                  <Camera className="h-3.5 w-3.5" /> {t('dialog.attachScreenshot')}
                </span>
                <span className="block text-label text-stevens-ink-3">
                  {t('dialog.attachHint')}
                </span>
              </span>
            </label>

            {attachShot && screenshot && (
              <div className="relative rounded-md border bg-card p-2">
                <img
                  src={screenshot}
                  alt={t('dialog.screenshotPreviewAlt')}
                  className="block w-full max-h-44 object-contain rounded"
                />
                <button
                  type="button"
                  onClick={() => handleToggleAttach(false)}
                  aria-label={t('dialog.removeScreenshot')}
                  className="absolute top-3 right-3 rounded-full bg-background/90 border p-1 hover:bg-muted"
                  disabled={submitting}
                >
                  <XIcon className="h-3 w-3" />
                </button>
                <p className="mt-1.5 font-mono text-meta text-stevens-ink-3">
                  {t('dialog.screenshotSize', { size: formatNumber(Math.round(screenshot.length / 1024)) })}
                </p>
              </div>
            )}

            {attachShot && !screenshot && capturing && (
              <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-caption text-stevens-ink-3">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('dialog.capturing')}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={submitting || capturing}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || capturing || message.trim().length < 3 || (attachShot && !screenshot)}
            variant="brand"
          >
            {submitting ? t('dialog.sending') : t('dialog.send')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
